import { EditorErrorBoundary } from "@prismicio/editor-fields"
import { useRequest } from "@prismicio/editor-support/Suspense"
import {
  Box,
  Button,
  DefaultErrorMessage,
  dialogErrorImage,
  Skeleton,
  Tooltip,
} from "@prismicio/editor-ui"
import { useSuspenseQuery } from "@tanstack/react-query"
import { Suspense } from "react"

import { useRepositoryMetaTitle } from "../../app/routing"
import {
  getReleasePermissions,
  getReleaseSizeQueryOptions,
  type MigrationRelease,
} from "../../app/services/release"
import { useRepositoryMigrationRelease } from "../../document/documentRouting"
import { DocumentListBody } from "../DocumentListBody"
import { DocumentListBodySkeleton } from "../DocumentListBody/DocumentListBody"
import { DocumentListBodyLayout } from "../DocumentListBody/DocumentListBodyLayout"
import { useDocumentListData } from "../DocumentListBody/useDocumentListData"
import { DocumentListHeader } from "../DocumentListHeader/DocumentListHeader"
import { DocumentListLayout } from "../documentListLayout"
import { DocumentListSearchParamsProvider } from "../DocumentListSearchParamsProvider"
import { DocumentListSidePanel } from "../DocumentListSidePanel"
import { ReleaseNameInput } from "../releases/ReleaseNameInput"
import { ReleasePageHeader } from "../releases/ReleasePageHeader"
import { ReleasePreview } from "../releases/ReleasePreview"
import { ReleasePublishButton } from "../releases/ReleasePublishButton"
import { ReleaseStatusIndicator } from "../releases/ReleaseStatusIndicator"
import { MigrationReleaseEmptyBody } from "./MigrationReleaseEmptyBody"

export function MigrationReleasePage() {
  useRepositoryMetaTitle("Migration release")

  return (
    <DocumentListSearchParamsProvider>
      <DocumentListLayout
        header={<DocumentListHeader tab={undefined} />}
        sidePanel={<DocumentListSidePanel selectedMenuItem="migration" />}
        body={
          <DocumentListBodyLayout>
            <EditorErrorBoundary renderError={renderMigrationReleasePageError}>
              <Suspense
                fallback={
                  <MigrationReleasePageSkeleton releaseId={undefined} />
                }
              >
                <MigrationReleaseBody />
              </Suspense>
            </EditorErrorBoundary>
          </DocumentListBodyLayout>
        }
      />
    </DocumentListSearchParamsProvider>
  )
}

interface MigrationReleaseListHeaderProps {
  migrationRelease: MigrationRelease
}

function MigrationReleaseHeader(props: MigrationReleaseListHeaderProps) {
  const { migrationRelease } = props

  const {
    data: { documents },
  } = useDocumentListData({
    tab: "upcoming",
    releaseId: migrationRelease.id,
  })

  const { canWrite } = useRequest(getReleasePermissions, [migrationRelease.id])

  // NOTE: Preview in migration does some weird write operations so the writer permission is needed
  const isPreviewAvailable = canWrite

  return (
    <ReleasePageHeader
      title={
        <>
          <ReleaseStatusIndicator release={migrationRelease} />

          <ReleaseNameInput
            defaultValue={migrationRelease.label}
            releaseId={migrationRelease.id}
          />
        </>
      }
      actions={
        <Box gap={8}>
          {isPreviewAvailable ? (
            <ReleasePreview
              releaseId={migrationRelease.id}
              releaseDocument={documents[0]}
              singlePreviewOption
            />
          ) : (
            <Tooltip content="You are missing rights to preview">
              <Button size="large" color="grey" startIcon="playCircle" disabled>
                Preview
              </Button>
            </Tooltip>
          )}
          <ReleasePublishButton
            buttonLabel="Publish this release now"
            release={migrationRelease}
            size="large"
          />
        </Box>
      }
    />
  )
}

function MigrationReleaseHeaderSkeleton() {
  return (
    <ReleasePageHeader
      title={<Skeleton width={290} height={40} />}
      actions={
        <Box gap={8}>
          <Skeleton width={253} height={40} />
          <Skeleton width={236} height={40} />
        </Box>
      }
    />
  )
}

function MigrationReleaseBody() {
  const migrationRelease = useRepositoryMigrationRelease()

  if (!migrationRelease) {
    return <MigrationReleaseEmptyBody />
  }

  return (
    <Suspense
      fallback={
        <MigrationReleasePageSkeleton releaseId={migrationRelease.id} />
      }
    >
      <MigrationReleaseTable migrationRelease={migrationRelease} />
    </Suspense>
  )
}

interface MigrationReleasesTableProps {
  migrationRelease: MigrationRelease
}

function MigrationReleaseTable(props: MigrationReleasesTableProps) {
  const { migrationRelease } = props

  const { data } = useSuspenseQuery(
    getReleaseSizeQueryOptions(migrationRelease.id),
  )
  const releaseSize = data.documentsCount

  if (releaseSize === 0) {
    return <MigrationReleaseEmptyBody />
  }

  return (
    <>
      <Suspense fallback={<MigrationReleaseHeaderSkeleton />}>
        <MigrationReleaseHeader migrationRelease={migrationRelease} />
      </Suspense>
      <DocumentListBody
        tab="upcoming"
        releaseId={migrationRelease.id}
        createEnabled={false}
      />
    </>
  )
}

function renderMigrationReleasePageError() {
  return (
    <Box
      height="100%"
      width="100%"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
    >
      <DefaultErrorMessage
        title="Request failed"
        description="An error occurred while fetching migration release."
        errorImageSrc={dialogErrorImage}
      />
    </Box>
  )
}

interface MigrationReleasePageSkeletonProps {
  releaseId?: string
}

function MigrationReleasePageSkeleton(
  props: MigrationReleasePageSkeletonProps,
) {
  const { releaseId } = props
  return (
    <>
      <MigrationReleaseHeaderSkeleton />
      <DocumentListBodySkeleton
        tab="upcoming"
        createEnabled={false}
        releaseId={releaseId}
      />
    </>
  )
}
